/**
 * Where the global tooltip panel sits relative to its trigger.
 *
 * Centered above/below is the default (tables, icon buttons). Triggers inside the
 * app sidebar must open to the **right** of the nav: the layer used to share z-index
 * 100 with the aside, so a centered panel was painted underneath it.
 */

export const TOOLTIP_MARGIN = 8;

export type TooltipMode = 'above' | 'below' | 'right';

export interface TooltipBox {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

export interface TooltipPlacement {
  x: number;
  y: number;
  mode: TooltipMode;
}

export function computeTooltipPlacement(
  trigger: TooltipBox,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  opts: { preferRight?: boolean; margin?: number } = {},
): TooltipPlacement {
  const margin = opts.margin ?? TOOLTIP_MARGIN;
  const vw = viewport.width;
  const vh = viewport.height;

  if (opts.preferRight) {
    const x = Math.min(
      trigger.right + margin,
      Math.max(margin, vw - margin - panel.width),
    );
    const half = panel.height / 2;
    const y = Math.min(
      Math.max(trigger.top + trigger.height / 2, margin + half),
      vh - margin - half,
    );
    return { x, y, mode: 'right' };
  }

  const fitsAbove = trigger.top > panel.height + margin * 2;
  const mode: TooltipMode = fitsAbove ? 'above' : 'below';
  const y = mode === 'above' ? trigger.top - margin : trigger.bottom + margin;
  const halfW = panel.width / 2;
  const x = Math.min(
    Math.max(trigger.left + trigger.width / 2, margin + halfW),
    vw - margin - halfW,
  );
  return { x, y, mode };
}
